import { formatDate } from '../../../libs/date';
import { parseError } from '../../../libs/parser';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CommonDescription from '../../../components/CommonDescription';
import Context from '../../../provider';
import ErrorIcon from '@mui/icons-material/Error';
import HeaderBarComponent from '../../../components/HeaderBarComponent';
import IconBenefitsRed from '../../../assets/hkdc/icons/BenefitsRedIcon';
import IconEdit from '../../../assets/hkdc/icons/EditIcon';
import IconHKDC from '../../../assets/hkdc/icons/HKDCIcon';
import IconMembershipProfile from '../../../assets/hkdc/icons/MembershipProfileIcon';
import IconRenewPayment from '../../../assets/hkdc/icons/RenewPaymentIcon';
import IconVoucher from '../../../assets/hkdc/icons/VoucherIcon';
import IconVoucherNone from '../../../assets/hkdc/icons/VoucherNoneIcon';
import MobileLogoFooterComponent from '../../../components/LogoFooterComponent';
import React, { useContext, useEffect, useRef, useState } from 'react';
import config from '../../../config';
import memberApi from '../../../libs/api/memberApi';
import useIsScrolling from '../../../hooks/useIsScrolling';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import './index.scss';

export default function MobileMembershipProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const notificationNotReadCount = useNotificationsCount();

  const scrollRef = useRef();
  const isScrolling = useIsScrolling(scrollRef.current);

  const { init, dispatch, tierMission, me, lang } = useContext(Context);

  const [tierContentData, setTierContentData] = useState({});
  const [vouchersData, setVouchersData] = useState([]);
  const [pageData, setPageData] = useState(null);

  const getItem = async (page) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.getItems({
        page,
        pageSize: 1000,
        status: 'unavailable'
      });
      setVouchersData((prevData) => [...prevData, ...(response?.list ?? []).filter((i) => !(prevData.find((p) => p.id === i.id)))]);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchTierContentData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const tierType = me.loyalty_account?.tier?.code.replace('tier-', '');
      const page = await memberApi.getPage({ ruri: `${tierType}-benefits` });
      setTierContentData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const fetchDescData = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const page = await memberApi.getPage({ ruri: 'profile-header' });
      setPageData(page);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  // This page only allow logged in user
  useEffect(() => {
    if (init) {
      getItem(1);
      fetchTierContentData();
      fetchDescData();
    }
  }, [init, me, navigate]);

  return (
    (
      <div className='mobile-membership-profile-page'>
        <HeaderBarComponent
          homeIcon={
            <IconHKDC
              style={{ width: '5rem', height: '2.78rem' }}
              onClick={(e) => {
                e.stopPropagation();
                window.location.assign('https://www.hkdesigncentre.org/ ');
              }}
            />
          }
        />
        <div ref={scrollRef} className="y-overflow content-container">
          <div className="title-icon-container">
            <div className="icon-container">
              <IconMembershipProfile
                className="icon"
                size="large" />
            </div>
            <div className="news-titles-container">
              <div className="title">
                {pageData?.sections[0].title[lang] ?? ''}
              </div>
              <div className="desc">
                <CommonDescription data={pageData?.sections[0].content[lang] ?? ''} />
              </div>
            </div>
          </div>

          <div className="membership-info-container">
            <div className="info-title-container">
              <div className="icon-container">
                <IconBenefitsRed
                  className="icon"
                  size="large" />
              </div>
              <div className="title">
                {t('profile.profile-title')}
              </div>
            </div>

            <div className="info-content-container">
              <div className="type-content-container">
                <div className="title">
                  {t('profile.name')}
                </div>
                {
                  me && me.loyalty_account &&
                  <div className="content">
                    {`${me.loyalty_account?.first_name} ${me.loyalty_account?.last_name}`}
                  </div>
                }
              </div>
              <div className="tier-info-cont">
                <div className="type-content-container">
                  <div className="title">
                    {t('profile.membership')}
                  </div>
                  {
                    me && me.loyalty_account &&
                    <div className="content">
                      {me.loyalty_account?.tier?.title?.[lang]}
                    </div>
                  }
                </div>
                {
                   me && me.loyalty_account?.tier_renewal &&
                   tierMission?.expire_at && me.loyalty_account?.tier_renewal.expire_from_now <= 30 &&
                  <div className="info-text-cont">
                    <ErrorIcon />
                    <div className="desc">
                      {`${(t('profile.expiry')).replace('{{date}}', formatDate(tierMission?.expire_at, lang))}`}
                    </div>
                  </div>
                }
              </div>
              {
                   me && me.loyalty_account?.tier_renewal &&
                   tierMission?.expire_at && me.loyalty_account?.tier_renewal.expire_from_now <= 30 &&
                  <div className="edit-btn-container"
                    style={{ backgroundColor: '#000000' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`${config.basename}renewal`);
                    }}
                  >
                    <div className="text-item">
                      {t('home-after.shortcut-3')}
                    </div>
                    <IconRenewPayment
                      className="icon"
                      size="large" />
                  </div>
              }

              <div className="type-content-container">
                <div className="title">
                  {t('profile.email')}
                </div>
                {
                  me &&
                  <div className="content">
                    {me.email}
                  </div>
                }
              </div>
              <div className="edit-btn-container"
                style={{ backgroundColor: '#004899' }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${config.basename}setting/profile/user`);
                }}
              >
                <div className="text-item">
                  {t('profile.btn')}
                </div>
                <IconEdit
                  className="icon"
                   size="large" />
              </div>
            </div>
          </div>

          <div className="benefits-detail-container">
            <div className="title-cont">
              {
                me && me.loyalty_account &&
                <div className="title">
                  {`${t('profile.tier-title')} - ${me.loyalty_account?.tier?.title?.[lang]}`}
                </div>
              }
            </div>
            <div className='list-cont'>
              {
                tierContentData.sections && tierContentData.sections.length > 0 && tierContentData.sections.map((item, index) => {
                  return (
                    <div key={index} className='item'>
                      <div className='item-img' style={{ backgroundImage: `url(${item.image[lang]})` }} />
                      <div className='item-content'>
                        <div className='item-text-title'>
                          {item.title[lang]}
                        </div>
                        <div className='item-text-desc'>
                          <CommonDescription data={item.content[lang]} />
                        </div>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>

          <div className="used-vouchers-list-container">
            <div className="list-tile-container">
              <div className="icon-container">
                <IconBenefitsRed
                  className="icon"
                  size="large" />
              </div>
              <div className="title-text">
                {t('profile.voucher-title')}
              </div>
            </div>
            {
              vouchersData.length > 0 && vouchersData.map((item, index) => {
                return (
                  <div key={index} className="used-vouchers-item-container">
                    <div className="item-title-container">
                      <div className="icon-container">
                        <IconVoucher
                          className="icon"
                          size="large" />
                      </div>
                      <div className="title-text">
                        {item.title[lang]}
                      </div>
                    </div>
                    <div className="time-state-container">
                      <div className="time-text">
                        {`${(t('profile.voucher-time')).replace('{{time}}', formatDate(item.expire_at, lang))}`}
                      </div>
                      <div className="state-text">
                        { item.usage?.percentage === 100 ? t('profile.voucher-used') : t('profile.voucher-expired') }
                      </div>
                    </div>
                  </div>
                );
              })
            }
            {
              (!vouchersData || vouchersData.length === 0) &&
              <div className="voucher-list-none">
                <IconVoucherNone
                  className="icon"
                  size="large" />
                <div className="text">
                  {t('none-data.coupon')}
                </div>
              </div>
            }
          </div>
          <MobileLogoFooterComponent />
          <div style={{ height: '10rem', backgroundColor: '#000000' }} />
        </div>
      </div>
    )
  );
}